/**
 * `@modulus/api` — the only thing in this repo that talks to the API.
 *
 * ```ts
 * import { createApi } from '@modulus/api'
 *
 * export const api = createApi()                       // student app, dashboard
 * export const api = createApi({ store: sessionStorageSession() })  // admin panel
 * ```
 *
 * The default base URL is `/api/v1` — same-origin, served by the BFF worker. No app
 * should ever configure an absolute API origin: the whole point of the proxy is that
 * a browser never talks to the API cross-origin, because the API sends no CORS
 * headers and is not going to.
 */

export { ApiClient, ApiError, SessionExpiredError, errorFrom } from './core/client'
export type { ClientConfig, RequestOptions } from './core/client'

export {
  localStorageSession,
  sessionStorageSession,
  memorySession,
} from './core/session'
export type { SessionStore, StoredSession } from './core/session'

export { streamFrames } from './core/sse'
export type { ChatStreamFrame, QuestionStreamFrame, StreamOptions } from './core/sse'

export { AuthResource } from './resources/auth'
export { LearnerResource } from './resources/learner'
export { TestsResource } from './resources/tests'
export type { Retrievability, TestQuestionFrame } from './resources/tests'
export { AiSolveResource, ChatsResource } from './resources/ai'
export { TeacherResource, SchoolAdminResource } from './resources/teacher'
export type { MemberQuery } from './resources/teacher'
export { PlatformResource } from './resources/platform'

export * from './types'

import { ApiClient } from './core/client'
import type { ClientConfig } from './core/client'
import { AuthResource } from './resources/auth'
import { LearnerResource } from './resources/learner'
import { TestsResource } from './resources/tests'
import { AiSolveResource, ChatsResource } from './resources/ai'
import { TeacherResource, SchoolAdminResource } from './resources/teacher'
import { PlatformResource } from './resources/platform'

export interface Api {
  client: ApiClient
  auth: AuthResource
  learner: LearnerResource
  tests: TestsResource
  aiSolve: AiSolveResource
  chats: ChatsResource
  teacher: TeacherResource
  schools: SchoolAdminResource
  platform: PlatformResource
}

export function createApi(config: ClientConfig = {}): Api {
  const client = new ApiClient(config)
  return {
    client,
    auth: new AuthResource(client),
    learner: new LearnerResource(client),
    tests: new TestsResource(client),
    aiSolve: new AiSolveResource(client),
    chats: new ChatsResource(client),
    teacher: new TeacherResource(client),
    schools: new SchoolAdminResource(client),
    platform: new PlatformResource(client),
  }
}
